import React from 'react'
import { Eye, RefreshCcw } from 'lucide-react'

import type { MessageItem, SelectOption } from '../../console-types'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { cn } from '../../lib/utils'
import {
  ControlledSelectField,
  formatTime,
  messageDirectionBadgeVariant,
  messageKindBadgeVariant,
} from './shared'

export function MessagesTab({
  messages,
  messageTypeFilter,
  setMessageTypeFilter,
  messageTypeOptions,
  messageSearch,
  setMessageSearch,
  isLoadingMessages,
  onRefresh,
  onOpenDetail,
}: {
  messages: MessageItem[]
  messageTypeFilter: string
  setMessageTypeFilter: (value: string) => void
  messageTypeOptions: SelectOption[]
  messageSearch: string
  setMessageSearch: (value: string) => void
  isLoadingMessages: boolean
  onRefresh: () => void
  onOpenDetail: (title: string, data: unknown, description?: string) => void
}) {
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-xl font-bold">Mensagens</h2>
          <p className="mt-0.5 font-mono text-xs text-muted-foreground">
            {messages.length} mensagem(ns) encontrada(s)
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onRefresh}
          disabled={isLoadingMessages}
          className="gap-2"
        >
          <RefreshCcw className={cn('size-3.5', isLoadingMessages && 'animate-spin')} />
          Atualizar
        </Button>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <ControlledSelectField
          value={messageTypeFilter}
          onValueChange={setMessageTypeFilter}
          placeholder="Todos os tipos"
          className="sm:w-48"
          options={messageTypeOptions}
        />
        <Input
          type="search"
          value={messageSearch}
          onChange={(event) => setMessageSearch(event.target.value)}
          className="bg-input font-mono text-xs sm:flex-1"
          placeholder="Buscar por EndToEndId, BizMsgIdr..."
        />
      </div>

      <div className="rounded-lg border border-border/60 bg-card/60 overflow-hidden">
        {!messages.length ? (
          <div className="px-5 py-8 text-center font-mono text-sm text-muted-foreground">
            {isLoadingMessages ? 'carregando...' : '— nenhuma mensagem encontrada —'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-border/60 bg-muted/40">
                  <th className="px-4 py-3 text-left font-display text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                    #
                  </th>
                  <th className="px-4 py-3 text-left font-display text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                    Tipo
                  </th>
                  <th className="px-4 py-3 text-left font-display text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                    Direção
                  </th>
                  <th className="px-4 py-3 text-left font-display text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                    EndToEndId
                  </th>
                  <th className="px-4 py-3 text-left font-display text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                    BizMsgIdr
                  </th>
                  <th className="px-4 py-3 text-left font-display text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                    Criação
                  </th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {messages.map((message) => (
                  <tr
                    key={message.index}
                    className="border-b border-border/30 transition-colors hover:bg-secondary/20 last:border-0"
                  >
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground/60">
                      {message.index}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={messageKindBadgeVariant(message.parsedType)}>
                        {message.parsedType}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={messageDirectionBadgeVariant(message.direction)}>
                        {message.direction}
                      </Badge>
                    </td>
                    <td className="max-w-[180px] truncate px-4 py-3 font-mono text-xs text-muted-foreground">
                      {message.endToEndId}
                    </td>
                    <td className="max-w-[160px] truncate px-4 py-3 font-mono text-xs text-muted-foreground">
                      {message.bizMsgIdr ?? '—'}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                      {formatTime(message.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          onOpenDetail(
                            `Mensagem #${message.index} (${message.parsedType})`,
                            message,
                            `${message.direction} · ${message.endToEndId}`
                          )
                        }
                        className="h-7 gap-1.5 px-2.5 text-xs"
                      >
                        <Eye className="size-3.5" />
                        Ver
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
